"use client";
import { Input } from "@/ui/index";
import { Label } from "@cmp-src/primitives/label";
import { InputProps } from "@/ui/_to_replace/Input/Input";
import {
  EditResourceMultipleForm,
  RESOURCE_VALUE_NOT_EQUAL,
  ResourceValueNotEqualType
} from "@/features/reserve/components/resources/tabs/GeneralTabMultiple";
import { useToggle } from "@/lib/hooks/useToggle";
import { cn } from "@gettactic/helpers/src/css/cn";
import { areAll } from "@/lib/utils";
import { IResource } from "@gettactic/api";
import { useEffect, useLayoutEffect, useRef } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import { UseFormSetValue } from "react-hook-form";
import { IoArrowDown, IoArrowForward } from "react-icons/io5";

type CheckboxStates = true | false | ResourceValueNotEqualType;
type CheckboxProps = {
  value: CheckboxStates;
  onChange: (x: CheckboxStates) => void;
} & Omit<InputProps, "value" | "onChange">;

function Checkbox({ value, onChange, ...rest }: CheckboxProps) {
  const [, reRender] = useToggle(false);
  const ref = useRef(false);
  useLayoutEffect(() => {
    if (ref.current) {
      setTimeout(reRender, 0);
    }
    ref.current = false;
  }, [ref.current]);
  useEffect(() => {
    ref.current = true;
  }, [value]);
  return (
    <Input
      className="border border-tertiary shadow-none"
      ref={(input) => {
        if (!input) return;
        if (value === RESOURCE_VALUE_NOT_EQUAL) {
          input.indeterminate = true;
          input.checked = false;
        } else {
          input.indeterminate = false;
          input.checked = value;
        }
      }}
      aria-checked={value === RESOURCE_VALUE_NOT_EQUAL ? "mixed" : value}
      type="checkbox"
      onChange={(ev) => {
        ev.preventDefault();
        const newVal =
          value === RESOURCE_VALUE_NOT_EQUAL
            ? false
            : value
              ? RESOURCE_VALUE_NOT_EQUAL
              : true;
        onChange(newVal);
        ref.current = true;
      }}
      {...rest}
    />
  );
}

type Props = {
  defaultValues: EditResourceMultipleForm;
  control: Control<EditResourceMultipleForm>;
  setValue: UseFormSetValue<EditResourceMultipleForm>;
  name: "is_restricted" | "is_approvable" | "is_unavailable" | "is_walkup";
  resources: IResource[];
  label: string;
  help: string;
  color?: "red" | "gray";
};

export function SelectEditBooleanMultipleValues({
  control,
  resources,
  setValue,
  name,
  label,
  help,
  color = "gray",
  defaultValues
}: Props) {
  const values = useWatch({ name: "bulkValues", control });
  const [, reRender] = useToggle(false);
  const [collapse, toggleCollapse] = useToggle(true);
  const allTheSame = areAll(values.map((x) => x[name]))
    ? values[0][name]
    : RESOURCE_VALUE_NOT_EQUAL;
  return (
    <div className="mt-4">
      <div className="mt-2 flex items-baseline gap-3 shadow-none">
        <Checkbox
          id={label}
          onChange={(x) => {
            const defaultValuesAllTheSame = areAll(
              defaultValues.bulkValues.map((x) => x[name])
            )
              ? defaultValues.bulkValues[0][name]
              : RESOURCE_VALUE_NOT_EQUAL;
            if (x !== RESOURCE_VALUE_NOT_EQUAL) {
              values.forEach((_, idx) =>
                setValue(`bulkValues.${idx}.${name}`, x)
              );
            } else if (defaultValuesAllTheSame !== RESOURCE_VALUE_NOT_EQUAL) {
              values.forEach((current, idx) =>
                setValue(`bulkValues.${idx}.${name}`, !allTheSame)
              );
            } else if (x === RESOURCE_VALUE_NOT_EQUAL) {
              values.forEach((_, idx) =>
                setValue(
                  `bulkValues.${idx}.${name}`,
                  defaultValues.bulkValues[idx][name]
                )
              );
            }
            setTimeout(reRender, 0);
          }}
          value={allTheSame}
        />
        <div>
          <Label
            htmlFor={label}
            className={cn(
              color === "red" ? "text-red-900" : "text-neutral-800",
              "relative -top-[0.25rem] cursor-pointer text-base font-bold"
            )}
          >
            {label}
          </Label>
          <p
            className={cn(
              color === "red" ? "text-red-900" : "text-neutral-800",
              "text-base"
            )}
          >
            {help}
          </p>
        </div>
      </div>
      <div
        className="cursor-pointer pt-1 text-tertiary text-xs"
        onClick={toggleCollapse}
      >
        {!collapse ? (
          <span>
            Hide
            <IoArrowDown className="inline h-2 w-2" />
          </span>
        ) : (
          <span>
            Show all ({resources.length}){" "}
            <IoArrowForward className="inline h-2 w-2" />
          </span>
        )}{" "}
      </div>
      <div className={cn(collapse ? "hidden" : "mt-2 ml-6")}>
        {values.map((x, idx) => (
          <Controller
            control={control}
            key={`bulkValues.${idx}.${name}` as const}
            name={`bulkValues.${idx}.${name}` as const}
            render={({ field: { onChange, value } }) => (
              <div>
                <label>
                  <Input
                    type="checkbox"
                    checked={x[name]}
                    onChange={(x) => onChange(x.currentTarget.checked)}
                  />{" "}
                  {resources[idx].name}{" "}
                </label>
              </div>
            )}
          />
        ))}
      </div>
    </div>
  );
}
